import {useCallback, useContext, useMemo} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {EventCreateContext} from '~/common/FabricInvitation/EventCreateDesign/EventCreateContext';
import {selectBackgrounds} from '~/common/FabricInvitation/reducers/fabricTemplate/selectors';
import {eventIsPremium} from '~/common/fabric/reducers/details/selectors';
import {
  RESET_DESIGN_OPTIONS,
  UPDATE_DESIGN_OPTIONS,
} from '~/common/fabric/reducers/fabricDesign/constants';
import {
  selectBackgroundId,
  selectHasBackgroundChanged,
  selectInitBackgroundColor,
  selectPremiumFeaturesPristine,
  selectShouldTogglePremiumBackground,
} from '~/common/fabric/reducers/fabricDesign/selectors';
import {isAllowedVideoFormat} from '~/common/fabric/utils';

export const useBackground = (isMobile = false) => {
  const dispatch = useDispatch();
  const backgrounds = useSelector(selectBackgrounds);
  const backgroundId = useSelector(selectBackgroundId);
  const initBackgroundColor = useSelector(selectInitBackgroundColor);
  const isPremium = useSelector(eventIsPremium);
  const premiumFeaturesPristine = useSelector(selectPremiumFeaturesPristine);
  const shouldShowPremiumBackground = useSelector(selectShouldTogglePremiumBackground);
  const {setBkgColor} = useContext(EventCreateContext);
  const hasBackgroundChanged = useSelector(selectHasBackgroundChanged);

  const onBackgroundReset = useCallback(() => {
    setBkgColor(initBackgroundColor || '#c0c0c0');
    dispatch({
      type: RESET_DESIGN_OPTIONS,
      payload: ['backgroundColor', 'backgroundId'],
    });
  }, [initBackgroundColor, setBkgColor, dispatch]);

  const backgroundIcons = useMemo(
    () =>
      backgrounds
        ?.filter(({media}) => media.find(({label}) => label === 'desktop').url)
        ?.map(({media}) => media.find(({label}) => label === 'icon').url),
    [backgrounds]
  );

  const onColorChange = useCallback(
    (background) => {
      dispatch({
        type: UPDATE_DESIGN_OPTIONS,
        payload: {
          backgroundColor: background,
          backgroundId: null,
          premiumFeaturesPristine: false,
          pristineBackground: false,
        },
      });
    },
    [dispatch]
  );

  const onTextureChange = useCallback(
    (icon) => {
      const selected = backgrounds?.find(
        ({media}) => media.find(({label}) => label === 'icon').url === icon
      );
      dispatch({
        type: UPDATE_DESIGN_OPTIONS,
        payload: {
          backgroundId: selected ? selected.id : null,
          backgroundColor: null,
          premiumFeaturesPristine: false,
          pristineBackground: false,
        },
      });
    },
    [backgrounds, dispatch]
  );

  const selectedBackground = useMemo(() => {
    let selected = false;
    // Only allow default background images/animations for premium templates
    // or freemium coming from the gallery upsell
    if (isPremium || !premiumFeaturesPristine || shouldShowPremiumBackground)
      selected = backgrounds?.find(({id}) => id === backgroundId);
    if (selected) {
      const mode = isMobile ? 'mobile' : 'desktop';
      const src = selected.media.find(({label}) => label === mode).url;
      const icon = selected.media.find(({label}) => label === 'icon').url;
      const isVideo = isAllowedVideoFormat(src);

      return {...selected, src, icon, isVideo};
    }
    return undefined;
  }, [
    backgrounds,
    backgroundId,
    isPremium,
    premiumFeaturesPristine,
    shouldShowPremiumBackground,
    isMobile,
  ]);

  return {
    backgroundIcons,
    onColorChange,
    onTextureChange,
    onBackgroundReset,
    selectedBackground,
    hasBackgroundChanged,
  };
};
